// Paged catalog-row fetch for the navigational index's per-engine body
// fetchers.
//
// Every body query of every engine goes through here instead of issuing one
// un-paged `executeQueryV2`. Three things come with that:
//
//   - **The same lane as the schema snapshot.** `fetchIntrospectionPage`
//     queues on `introspectionSemaphore`'s two-slot per-connection gate, so a
//     nav page can never starve the user's own query — or the snapshot's
//     phase-1 tables query, which is what the pre-paging feed was racing.
//   - **`NAV_INDEX_PAGE_ROWS`-sized pages.** Bodies are large; a 500-row page
//     of definitions is already megabytes. Nothing ships as a single
//     multi-MB result set any more.
//   - **A shared body-byte budget.** Paging stops the moment the build's
//     allowance is gone, and the caller marks the index `partial`.
//
// Ordering is by a STABLE key per engine (`object_id`, `oid`, `rowid`, or the
// qualified name where the catalog exposes no surrogate), spliced into an
// `ORDER BY` the paging clause is appended to. Without a total order OFFSET
// paging silently drops and duplicates rows.
//
// Legacy-server fallback: if the FIRST page query fails, the helper retries
// once with the un-paged statement. Oracle only learned `OFFSET … ROWS FETCH
// NEXT … ROWS ONLY` in 12.1 (pre-12c raises ORA-00933), and the nav feed has
// no server-version probe of its own; the retry keeps those servers indexing
// instead of silently producing an empty index. The fallback is still capped
// at the page `maxRows` guard by `fetchIntrospectionPage`, so it cannot ship
// an unbounded result set either.

import {
  fetchIntrospectionPaged,
  type IntrospectionRow,
} from '@/features/connections/services/language-services/introspectionPaging';
import {
  fetchIntrospectionPage,
  pageClauseFor,
  type PagingDialect,
} from '@/features/connections/services/language-services/introspectionQuery';
import type { IntrospectionTruncationEngine } from '@/features/connections/services/language-services/introspectionTruncation';

import {
  NAV_INDEX_PAGE_ROWS,
  NAV_INDEX_ROW_CEILING,
  navBodyBytesOf,
  type NavBodyBudget,
} from '../navIndexBudget';

export type { IntrospectionRow } from '@/features/connections/services/language-services/introspectionPaging';

export type NavPagedRowsQuery = {
  readonly connectionId: string;
  /** The `database` argument of `executeQueryV2` — Oracle passes its schema. */
  readonly database: string;
  readonly engine: IntrospectionTruncationEngine;
  /** Short slug for the executionId (`mssql-modules`, `pg-views`, …). */
  readonly label: string;
  /** Base statement: everything up to (but not including) `ORDER BY`. */
  readonly sql: string;
  /**
   * Stable total-order key, spliced verbatim into `ORDER BY`. Must be unique
   * per row or OFFSET paging will drop/duplicate rows across page boundaries.
   */
  readonly orderBy: string;
  readonly dialect: PagingDialect;
  /** Columns whose text counts against the build's body-byte budget. */
  readonly bodyColumns: readonly string[];
  /** Shared per-build allowance. Omitted in single-artefact unit tests. */
  readonly budget?: NavBodyBudget;
  /** `true` stops paging where it stands (connection teardown). */
  readonly shouldAbort?: () => boolean;
  /**
   * Called when the listing is NOT authoritative: a page query failed (the
   * rows read before it are still returned), the first page and the un-paged
   * fallback both failed, the row ceiling stopped the paging, or the bridge
   * truncated a result. A consumer that REPLACES records from the listing
   * (the DDL invalidator's constraint rebuild) keeps what it has instead; the
   * connection-wide build marks the index partial. Not called for an abort or
   * a spent body-byte budget — the budget reports those itself.
   */
  readonly onIncomplete?: () => void;
  /**
   * The WHY behind `onIncomplete`, for the consumers that handle the two
   * reasons differently. Fired ONLY when a catalog QUERY failed — a page plus
   * its un-paged fallback, or a later page — never for a cap (the row
   * ceiling, a truncated result), which is a deliberate stop at a known line.
   * Always accompanied by `onIncomplete`, so a consumer that treats every
   * short listing alike needs only that one. `buildIndexForConnection` REJECTS
   * on this signal: a lost-to-failure slice of the catalog is unbounded, so
   * fulfilling readiness over the rest would report "no references found" for
   * whatever it held and suppress the retry that would fix it.
   */
  readonly onFetchFailed?: () => void;
  /** Overridable for tests. */
  readonly pageRows?: number;
};

/**
 * Page one nav catalog query end to end and return every row it yielded.
 * Rows accumulate rather than streaming to a sink because the per-engine
 * fetchers assemble whole-object listings from them (Oracle reassembles
 * `ALL_SOURCE` lines per object, MySQL synthesizes `<table>.<column>` default
 * names), and a page boundary must not split an object.
 */
export async function fetchNavRowsPaged(
  query: NavPagedRowsQuery,
): Promise<readonly IntrospectionRow[]> {
  const rows: IntrospectionRow[] = [];
  const pageRows = query.pageRows ?? NAV_INDEX_PAGE_ROWS;

  const sqlForPage = (pageClause: string): string =>
    `${query.sql}\nORDER BY ${query.orderBy}${pageClause ? `\n${pageClause}` : ''}`;

  const outcome = await fetchIntrospectionPaged<IntrospectionRow>({
    fetchPage: (page) =>
      fetchIntrospectionPage({
        engine: query.engine,
        connectionId: query.connectionId,
        database: query.database,
        label: query.label,
        sql: sqlForPage(pageClauseFor(query.dialect, page)),
      }),
    aggregate: (pageRowsIn) => [...pageRowsIn],
    sink: (items) => {
      for (const row of items) {
        rows.push(row);
        query.budget?.spend(navBodyBytesOf(row, query.bodyColumns));
      }
    },
    totalBudget: NAV_INDEX_ROW_CEILING,
    pageRows,
    shouldAbort: () => query.shouldAbort?.() === true || query.budget?.isExhausted() === true,
  });

  // The row ceiling (or a truncated page) is the same user-visible outcome
  // as the byte budget: we stopped before the catalog ran out. Fold it into
  // the budget flag the builder reads — and tell a budget-less consumer too.
  if (outcome.capHit) {
    query.budget?.exhaust();
    query.onIncomplete?.();
  }

  if (outcome.failed && outcome.pages === 0) {
    const fallback = await fetchUnpagedFallback(query, sqlForPage(''));
    // A fallback that RAN and was truncated is a cap, not a failure: the
    // statement was accepted and the rows we have stop at a known line.
    if (!fallback.ok) reportFetchFailed(query);
    else if (fallback.truncated) query.onIncomplete?.();
    return fallback.rows;
  }
  if (outcome.failed) reportFetchFailed(query);
  return rows;
}

/** A catalog query failed. Both signals fire: `onIncomplete` for the
 *  consumers that only care that the listing is short, `onFetchFailed` for
 *  those that must tell a failure from a cap (see the type's doc). */
function reportFetchFailed(query: NavPagedRowsQuery): void {
  query.onFetchFailed?.();
  query.onIncomplete?.();
}

/**
 * One un-paged attempt at the same statement, for a server that rejects the
 * row-limiting clause (see the module header's Oracle note). Still goes
 * through the introspection lane and still carries the page `maxRows` guard.
 */
async function fetchUnpagedFallback(
  query: NavPagedRowsQuery,
  sql: string,
): Promise<{ readonly ok: boolean; readonly rows: readonly IntrospectionRow[]; readonly truncated?: boolean }> {
  // An abort or a spent budget is not a failure — the budget reports those.
  if (query.shouldAbort?.() === true || query.budget?.isExhausted() === true) return { ok: true, rows: [] };
  // No bridge means the paged attempt failed for that reason, not the SQL —
  // retrying would just burn another no-op (test mode without the preload).
  if (typeof window === 'undefined' || !window.electronAPI?.executeQueryV2) return { ok: false, rows: [] };
  const page = await fetchIntrospectionPage({
    engine: query.engine,
    connectionId: query.connectionId,
    database: query.database,
    label: `${query.label}-unpaged`,
    sql,
  });
  if (!page.ok) return { ok: false, rows: [] };
  for (const row of page.rows) query.budget?.spend(navBodyBytesOf(row, query.bodyColumns));
  if (page.truncated) query.budget?.exhaust();
  return { ok: true, rows: page.rows, truncated: page.truncated === true };
}
